import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { AssetStatus } from "components/status/AssetStatus";
import { Intent, Position } from "@blueprintjs/core";
import { CheckService, FleetService } from "services";
import { AlertConfirm, Modal, NotificationToaster } from "components/notifications";
import { EventPanel } from "pages/asset/events/shared/EventPanel";
import { FormHeading } from "components/form-fields";
import { DateToLocal } from "components/dates";
import { UserHasAccess } from "components/user-access";
import axios from "axios";
import { ModalSize } from 'components/notifications/ModalSize';

export function EventViewModal({
    eventId,
    eventTypeId,
    eventOptions,
    isOpen = false,
    onModalClose,
    requiredAction = null,
    asset,
    mappedAssetOptions,
    loading,
    refetchEvents,
    eventTypesForAsset,
    openEditEventModal
}) {
    const userHasAccess = requiredAction ? UserHasAccess(requiredAction) : true;

    const [loadingData, setLoadingData] = useState(true);
    const [modalTitle, setModalTitle] = useState(<></>);
    const [event, setEvent] = useState(null);
    const [eventType, setEventType] = useState(null);
    const [eventStatus, setEventStatus] = useState(null);
    const [detailedChecks, setDetailedChecks] = useState([]);
    const [matchedEventTypeForAsset, setMatchedEventTypeForAsset] = useState(null);
    const [showReopenConfirm, setShowReopenConfirm] = useState(false);
    
    useEffect(() => {
        if (!isOpen || !userHasAccess || !eventId || !eventTypeId || !eventOptions) {
            return;
        }

        setLoadingData(true);

        axios.all([
            FleetService.getEvent(eventId),
            FleetService.getEventType(eventTypeId)
        ]).then(axios.spread((
            getEventResponse,
            getEventTypeResponse
        ) => {
            setEvent(getEventResponse);
            setEventType(getEventTypeResponse);

            if (getEventResponse && eventOptions?.eventStatuses) {
                let matchedStatus = eventOptions.eventStatuses.find(
                    s => s.name.toLowerCase() === getEventResponse.statusName?.toLowerCase()
                );

                setEventStatus(matchedStatus);
            }
        })).catch(() => {
            NotificationToaster.show(Intent.DANGER, `Failed to load event data. Please refresh the page.`);
        }).finally(() => {
            setLoadingData(false);
        });
    }, [eventId, eventTypeId, eventOptions, isOpen, userHasAccess]);

    useEffect(() => {
        if (!eventTypeId || !eventTypesForAsset) {
            return;
        }

        let matched = eventTypesForAsset.find(
            et => et.eventTypeId === eventTypeId
        );
        setMatchedEventTypeForAsset(matched);
    }, [eventTypeId, eventTypesForAsset]);

    useEffect(() => {
        if (!event?.eventDate || !asset || !mappedAssetOptions || loading || loadingData) {
            return;
        }
        
        fetchDetailedChecks(event);

        setModalTitle(
            <div className="inline-items">
                <FormHeading headingLevel="h2">Event for </FormHeading>
                <AssetStatus
                    statusId={asset?.assetLocation?.assetStatus?.id}
                    ownershipId={asset?.assetLocation?.assetOwnership?.id}
                    isLoading={loadingData || loading}
                    statusAttributeMappings={mappedAssetOptions?.assetStatusesMappings}
                    ownershipAttributeMappings={mappedAssetOptions?.ownershipAttributeMappings}
                    tooltipPosition={Position.RIGHT}
                />
                <FormHeading headingLevel="h2" className="text-intent-primary">{asset?.registration}</FormHeading>
                <FormHeading headingLevel="h2">on {<DateToLocal format='DD MMM YYYY'>{event?.eventDate}</DateToLocal>}</FormHeading>
            </div>
        );
    }, [event, asset, mappedAssetOptions, loading, loadingData]);

    function fetchDetailedChecks(eventObj) {
        if (!eventObj?.checks?.length) {
            setDetailedChecks([]);
            return;
        }

        setDetailedChecks([]);
        axios.all(eventObj.checks.map(c => CheckService.getCheck(c.checkId)))
            .then(responses => {
                setDetailedChecks(responses);
            })
            .catch(() => {
                setDetailedChecks([]);
            });
    }

    function handleCancel() {
        if (onModalClose) {
            onModalClose();
        }
    }

    function handleDelete(id) {
        setLoadingData(true);

        FleetService.deleteEvent(id).then(() => {
            NotificationToaster.show(Intent.SUCCESS, "Event deleted successfully.");
        }).catch((er) => {
            const VALIDATION_ERROR_STATUS = 400;
            if (er.response.status === VALIDATION_ERROR_STATUS) {
                NotificationToaster.show(Intent.WARNING, er.response.data);
                return;
            }

            NotificationToaster.show(Intent.DANGER, "Unable to delete the event, please try again.");
        }).finally(() => {
            refetchEvents?.();
            setLoadingData(false);
            handleCancel();
        });
    }

    function onShowReopenConfirm() {
        setShowReopenConfirm(true);
    }

    function confirmReopen() {
        FleetService.reopenEvent(event.eventId)
            .then(() => {
                NotificationToaster.show(Intent.SUCCESS, `Event reopened successfully.`);
                // refresh event data
                refreshEvent();
                refetchEvents?.();
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, `Failed to reopen event. Please try again.`);
            })
            .finally(() => {
                setShowReopenConfirm(false);
            });
    }

    function refreshEvent() {
        FleetService.getEvent(event.eventId)
            .then((getEventResponse) => {
                setEvent(getEventResponse);
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, `Failed to refresh event data. Please try again.`);
            });
    }

    function cancelReopen() {
        setShowReopenConfirm(false);
    }

    return (
        <Modal
            title={modalTitle}
            isOpen={isOpen}
            onClose={handleCancel}
            clickOutsideClose={true}
            updateStateOnClose={false}
            enforceFocus={false}
            size={ModalSize.LARGE}
            className="listing-table-scroll-vertical"
            fixedHeight={false}
            fixedHeightPercentage={50}
        >
            <EventPanel 
                event={event} 
                eventType={eventType} 
                eventStatus={eventStatus} 
                eventOptions={eventOptions}
                loadingData={loadingData} 
                onDelete={handleDelete}
                reopenFunction={onShowReopenConfirm}
                openEditEventModal={openEditEventModal}
                detailedChecks={detailedChecks}
                matchedEventTypeForAsset={matchedEventTypeForAsset}
            />

            <AlertConfirm
                title="Reopen Event"
                isOpen={showReopenConfirm}
                onConfirm={confirmReopen}
                onCancel={cancelReopen}
                confirmButtonIntent="danger"
                confirmButtonText="Reopen"
            >
                <p>This event has been signed off, reopening this event will reset signoff. Are you sure you want to reopen this event?</p>
            </AlertConfirm>
        </Modal>
    );
}

EventViewModal.propTypes = {
    eventId: PropTypes.number,
    eventTypeId: PropTypes.number,
    eventOptions: PropTypes.object,
    isOpen: PropTypes.bool.isRequired,
    onModalClose: PropTypes.func,
    requiredAction: PropTypes.string,
    asset: PropTypes.object,
    mappedAssetOptions: PropTypes.object,
    loading: PropTypes.bool,
    reFetchEvents: PropTypes.func,
    eventTypesForAsset: PropTypes.array,
    openEditEventModal: PropTypes.func
};